import React from 'react';
import { useNavigate } from 'react-router';
import { PLAN_DEFINITIONS } from '@/api-types';
import type { PlanDefinition, PlanTier } from '@/api-types';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { PlanCard } from './PlanCard';
import { PlanComparison } from './PlanComparison';
import { PLAN_ORDER } from './plan-order';

export default function PricingPage() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [currentTier, setCurrentTier] = React.useState<PlanTier | null>(null);
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (!user) return;

		apiClient.getUserOrgs().then((res) => {
			if (res.success && res.data?.orgs?.length) {
				const orgId = res.data.orgs[0].id;
				apiClient.getOrgSubscription(orgId).then((subRes) => {
					if (subRes.success && subRes.data?.plan) {
						setCurrentTier(subRes.data.plan.tier as PlanTier);
					}
				}).catch((err) => console.error('Failed to load subscription', err));
			}
		}).catch((err) => console.error('Failed to load user orgs', err));
	}, [user]);

	const handleSelectPlan = async (plan: PlanDefinition) => {
		if (!user) {
			navigate('/');
			return;
		}

		if (plan.isContactSales) {
			window.location.href = 'mailto:hello@hatchd.ai?subject=Enterprise%20Plan%20Inquiry';
			return;
		}

		if (plan.tier === 'free') return;

		setSubmitting(true);
		try {
			const orgsRes = await apiClient.getUserOrgs();
			if (!orgsRes.success || !orgsRes.data?.orgs?.length) {
				toast.error('No organization found. Please create one first.');
				return;
			}
			const orgId = orgsRes.data.orgs[0].id;
			const res = await apiClient.createCheckoutSession(orgId, plan.id);
			if (res.success && res.data?.url) {
				window.location.href = res.data.url;
			} else {
				toast.error('Failed to start checkout');
			}
		} catch {
			toast.error('Failed to start checkout');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="min-h-screen bg-bg-3">
			<main className="container mx-auto px-4 py-12 max-w-6xl">
				<div className="text-center mb-12">
					<h1 className="text-4xl font-bold font-[departureMono] text-red-500 mb-4">
						PRICING
					</h1>
					<p className="text-text-secondary text-lg max-w-2xl mx-auto">
						Choose the plan that fits your needs. All plans include AI-powered app generation.
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
					{PLAN_ORDER.map((tier) => (
						<PlanCard
							key={tier}
							plan={PLAN_DEFINITIONS[tier]}
							currentTier={currentTier}
							onSelect={handleSelectPlan}
							isAuthenticated={!!user}
							disabled={submitting}
						/>
					))}
				</div>

				<div className="mt-16">
					<h2 className="text-2xl font-bold text-center mb-8">Feature Comparison</h2>
					<PlanComparison />
				</div>
			</main>
		</div>
	);
}
